import React, { useState, useEffect, useRef } from "react";
import "./CheckoutModal.css";
import { Modal, StepProgress, Button, Input, Alert } from "../ui";
import { usePanier, getOutil, FRAIS_LIVRAISON_CENTIMES } from "../../PanierContext";
import { useAuth } from "../Context/AuthContext";

const SQUARE_APP_ID   = "sq0idp-rSubymCWY32C-lcptojsSA";
const SQUARE_LOCATION = "LRQ9HMBW4MQHD";

const STEPS = ["Livraison", "Paiement", "Confirmation"];

interface ShippingForm {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  complement: string;
  codePostal: string;
  ville: string;
}

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { panier, totalAvecLivraison, viderPanier } = usePanier();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Square state
  const [card, setCard]       = useState<any>(null);
  const [payments, setPayments] = useState<any>(null);
  const squareInitialized = useRef(false);

  // IDs Square
  const [customerId, setCustomerId] = useState("");
  const [orderId, setOrderId]       = useState("");

  const [form, setForm] = useState<ShippingForm>({
    nom: user?.name?.split(" ")[0] ?? "",
    prenom: user?.prenom ?? "",
    email: user?.email ?? "",
    telephone: user?.telephone ?? "",
    adresse: user?.adresse ?? "",
    complement: user?.complement ?? "",
    codePostal: user?.code_postal ?? "",
    ville: user?.ville ?? "",
  });

  // Pre-fill from user when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setForm((f) => ({
        ...f,
        email: user.email ?? f.email,
        nom: user.name ?? f.nom,
        prenom: user.prenom ?? f.prenom,
        telephone: user.telephone ?? f.telephone,
        adresse: user.adresse ?? f.adresse,
        complement: user.complement ?? f.complement,
        codePostal: user.code_postal ?? f.codePostal,
        ville: user.ville ?? f.ville,
      }));
    }
  }, [isOpen, user]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setError(null);
      squareInitialized.current = false;
    }
  }, [isOpen]);

  // Init Square on step 1
  useEffect(() => {
    if (step !== 1 || squareInitialized.current) return;

    const init = async () => {
      if (!(window as any).Square) {
        setError("Le module de paiement n'a pas pu se charger.");
        return;
      }
      try {
        squareInitialized.current = true;
        const paymentsInstance = (window as any).Square.payments(SQUARE_APP_ID, SQUARE_LOCATION);
        setPayments(paymentsInstance);
        const cardInstance = await paymentsInstance.card();
        const container = document.getElementById("sq-card-container");
        if (container) await cardInstance.attach(container);
        setCard(cardInstance);
      } catch (e) {
        console.error(e);
        setError("Erreur lors de l'initialisation du paiement.");
      }
    };
    init();
  }, [step]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  /* ── Étape 1 : créer user Square + commande ── */
  const submitShipping = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Créer le customer Square
      const userRes = await fetch("/backend/user.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          nom: form.nom, prenom: form.prenom, mail: form.email,
          telephone: form.telephone, adresse: form.adresse,
          complement_adresse: form.complement, code_postal: form.codePostal,
          ville: form.ville,
        }),
      });
      if (!userRes.ok) throw new Error("Erreur lors de la création du client.");
      const userData = await userRes.json();
      const cid = userData?.customer?.id ?? "";
      setCustomerId(cid);

      // Créer la commande Square
      const orderParams = new URLSearchParams({
        locationId: SQUARE_LOCATION,
        customerId: cid,
      });
      panier.forEach((item, i) => {
        const o = getOutil(item.idOutil);
        if (!o) return;
        orderParams.append(`line_items[${i}][name]`, o.nom);
        orderParams.append(`line_items[${i}][quantity]`, item.nombre.toString());
        orderParams.append(`line_items[${i}][base_price_money][amount]`, o.prix.toString());
        orderParams.append(`line_items[${i}][base_price_money][currency]`, "EUR");
      });
      const li = panier.length;
      orderParams.append(`line_items[${li}][name]`, "Frais de livraison");
      orderParams.append(`line_items[${li}][quantity]`, "1");
      orderParams.append(`line_items[${li}][base_price_money][amount]`, FRAIS_LIVRAISON_CENTIMES.toString());
      orderParams.append(`line_items[${li}][base_price_money][currency]`, "EUR");

      const orderRes = await fetch("/backend/order.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: orderParams,
      });
      if (!orderRes.ok) throw new Error("Erreur lors de la création de la commande.");
      const orderData = await orderRes.json();
      setOrderId(orderData?.order?.id ?? "");

      setStep(1);
    } catch (err: any) {
      setError(err.message || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Étape 2 : paiement ── */
  const submitPayment = async () => {
    if (!card || !payments) return;
    setLoading(true);
    setError(null);
    try {
      const tokenResult = await card.tokenize();
      if (tokenResult.status !== "OK") throw new Error("Erreur de tokenisation de la carte.");

      // SCA / 3DS
      const verificationResult = await payments.verifyBuyer(tokenResult.token, {
        amount: (totalAvecLivraison / 100).toFixed(2),
        currencyCode: "EUR",
        intent: "CHARGE",
        billingContact: {
          givenName: form.prenom || "Client",
          familyName: form.nom || "Inconnu",
          email: form.email || "",
          country: "FR",
        },
      });
      if (!verificationResult?.token) throw new Error("La vérification 3D Secure a échoué.");

      const payRes = await fetch("/backend/payment.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          locationId: SQUARE_LOCATION,
          sourceId: tokenResult.token,
          verificationToken: verificationResult.token,
          amount: totalAvecLivraison.toString(),
          customerId,
          orderId,
        }),
      });
      if (!payRes.ok) {
        const d = await payRes.json();
        throw new Error(d?.details?.errors?.[0]?.detail || "Le paiement a échoué.");
      }

      viderPanier();
      setStep(2);
    } catch (err: any) {
      setError(err.message || "Erreur lors du paiement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={step < 2 ? onClose : () => {}} title="Commander" size="lg">
      <StepProgress steps={STEPS} current={step} />

      {error && <Alert type="error">{error}</Alert>}

      {/* ── Step 0 : Infos livraison ── */}
      {step === 0 && (
        <form className="checkout-form" onSubmit={submitShipping}>
          <h3>Informations de livraison</h3>
          <div className="checkout-form__grid">
            <Input label="Nom" name="nom" value={form.nom} onChange={handleChange} required />
            <Input label="Prénom" name="prenom" value={form.prenom} onChange={handleChange} required />
            <Input label="Email" type="email" name="email" value={form.email} onChange={handleChange} required />
            <Input label="Téléphone" type="tel" name="telephone" value={form.telephone} onChange={handleChange} required />
          </div>
          <div className="checkout-form__grid checkout-form__grid--full">
            <Input label="Adresse" name="adresse" value={form.adresse} onChange={handleChange} required />
            <Input label="Complément (optionnel)" name="complement" value={form.complement} onChange={handleChange} />
          </div>
          <div className="checkout-form__grid">
            <Input label="Code postal" name="codePostal" value={form.codePostal} onChange={handleChange} required />
            <Input label="Ville" name="ville" value={form.ville} onChange={handleChange} required />
          </div>

          <div className="checkout-summary">
            <strong>Récapitulatif</strong>
            {panier.map((item) => {
              const o = getOutil(item.idOutil);
              return o ? (
                <div key={o.id} className="checkout-summary__line">
                  <span>{o.nom} × {item.nombre}</span>
                  <span>{((o.prix * item.nombre) / 100).toFixed(2)} €</span>
                </div>
              ) : null;
            })}
            <div className="checkout-summary__line">
              <span>Frais de livraison</span>
              <span>6.00 €</span>
            </div>
            <div className="checkout-summary__line checkout-summary__line--total">
              <span>Total</span>
              <span>{(totalAvecLivraison / 100).toFixed(2)} €</span>
            </div>
          </div>

          <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
            Continuer vers le paiement
          </Button>
        </form>
      )}

      {/* ── Step 1 : Paiement ── */}
      {step === 1 && (
        <div className="checkout-payment">
          <h3>Paiement sécurisé</h3>
          <p className="checkout-payment__total">
            Montant à payer : <strong>{(totalAvecLivraison / 100).toFixed(2)} €</strong>
          </p>
          <div id="sq-card-container" className="sq-card-container" />
          <p className="checkout-payment__notice">
            🔒 Vos données bancaires sont chiffrées et sécurisées par Square.
          </p>
          <div className="checkout-payment__actions">
            <Button variant="ghost" onClick={() => { setStep(0); setError(null); }}>
              ← Retour
            </Button>
            <Button variant="primary" size="lg" loading={loading} onClick={submitPayment}>
              Payer {(totalAvecLivraison / 100).toFixed(2)} €
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 2 : Confirmation ── */}
      {step === 2 && (
        <div className="checkout-confirmation">
          <div className="checkout-confirmation__icon">✅</div>
          <h3>Merci pour votre commande !</h3>
          <p>Un email de confirmation vous a été envoyé à <strong>{form.email}</strong>.</p>
          <p>Vos livrets seront expédiés dans les meilleurs délais.</p>
          <Button variant="primary" size="lg" onClick={onClose}>
            Retour à l'accueil
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default CheckoutModal;
